/**
 * Everything the console has read so far, in one place.
 *
 * A plain object, filled by live listeners, with a render on every change.
 *
 * The listeners are opened once for the whole session rather than per screen:
 * six screens each opening their own on `units` would be six reads of the same
 * collection.
 */

import {
  CHAT_UNREAD_WINDOW,
  DEFAULT_CHAT_SETTINGS,
  EVERYTHING,
  addPeriods,
  currentNotices,
  arrears,
  overview,
  periodOf,
  unitMap,
} from "@liyu/core";
import type {
  Arrears,
  Entitlements,
  HeldDoc,
  LedgerEntry,
  Overview,
  Period,
  PeriodSummary,
  TelebirrOrder,
  InboxItem,
  ChapaOrder,
  ChapaReceipt,
  UnitMap,
} from "@liyu/core";
import { where } from "firebase/firestore";
import * as data from "./data";
import type { Session } from "./session";

export interface Store {
  period: Period;
  orgName: string;
  /** What the complex's plan includes; everything until its document says otherwise. */
  entitlements: Entitlements;
  /** Whether residents here can pay with Telebirr: switched on, and in the plan. */
  telebirr: boolean;
  /** The Telebirr order a resident came back from, followed until it settles. */
  telebirrOrder: { id: string; order: TelebirrOrder | null } | null;
  /** Whether residents here can pay with Chapa: the committee's subaccount is switched on. */
  chapa: boolean;
  /** The Chapa order a resident came back from, followed until it settles. */
  chapaOrder: { id: string; order: ChapaOrder | null } | null;
  /** Payments Chapa confirmed, newest first, for the accountant and the committee. */
  chapaReceipts: ChapaReceipt[];
  units: data.Unit[];
  summaries: Map<string, PeriodSummary>;
  months: { unitId: string; summary: PeriodSummary }[];
  waiting: data.Payment[];
  /** One unit's payments, for the details panel that is open, if one is. */
  unitPayments: { unitId: string; rows: data.Payment[] } | null;
  mine: data.Payment[];
  /** The resident's own ledger, for what each payment settled and for pricing months ahead. */
  entries: LedgerEntry[];
  /** What the resident's payments brought beyond what was owed. */
  extras: HeldDoc[];
  /** The resident's own unit's monthly fee, or 0 until it is read. */
  monthlyDue: number;
  expenses: data.Expense[];
  complaints: data.Complaint[];
  notices: data.Notice[];
  campaigns: data.Campaign[];
  changes: data.Change[];
  /** Account uid to the door number or staff id it belongs to. */
  people: Map<string, string>;
  /** Every account in the complex, for the accountant's unit page. */
  logins: data.Login[];
  held: data.Held[];
  reminders: data.Reminder[];
  /** What Liyu has told the person signed in, newest first. */
  inbox: InboxItem[];
  advertisements: data.Advertisement[];
  accounts: data.Account[];
  building: data.Building | null;
  /** The building's chat, read only while it is on screen. */
  chat: ChatRoom;
  /** The chat's newest few messages, for the unread count in the band. */
  chatRecent: data.ChatMessage[];
  /** A read the server turned down, named. Never swallowed. */
  problem: Error | null;
  /** False until every listener this role opened has answered once. */
  ready: boolean;
  /**
   * The listeners that have answered at least once, by name.
   *
   * `ready` is the whole set and is still what a view falls back to, but a
   * screen rarely needs the whole set. The journal needs the change log and
   * nothing else; making it wait on the month's expenses as well is how one
   * slow collection ends up blanking a screen that had its answer already.
   */
  loaded: Set<string>;
}

export interface ChatRoom {
  /** Oldest first, the last few dozen. */
  messages: data.ChatMessage[];
  /** Pinned by the committee, newest first, however old. */
  pinned: data.ChatMessage[];
  /** The reader's own membership, or null until they have joined. */
  me: data.ChatMember | null;
  settings: data.ChatSettings;
  /** Everybody who has joined, for the committee. Empty for everybody else. */
  members: data.ChatMember[];
  /**
   * A read of the room the server turned down. Kept here rather than in
   * `problem`, so a refused chat says so in the chat and leaves the rest of
   * the console standing.
   */
  problem: Error | null;
}

function emptyRoom(): ChatRoom {
  return {
    messages: [],
    pinned: [],
    me: null,
    settings: DEFAULT_CHAT_SETTINGS,
    members: [],
    problem: null,
  };
}

export const store: Store = {
  period: periodOf(new Date()),
  orgName: "",
  entitlements: EVERYTHING,
  telebirr: false,
  telebirrOrder: null,
  chapa: false,
  chapaOrder: null,
  chapaReceipts: [],
  units: [],
  summaries: new Map(),
  months: [],
  waiting: [],
  unitPayments: null,
  mine: [],
  entries: [],
  extras: [],
  monthlyDue: 0,
  expenses: [],
  complaints: [],
  notices: [],
  campaigns: [],
  changes: [],
  people: new Map(),
  logins: [],
  held: [],
  reminders: [],
  inbox: [],
  advertisements: [],
  accounts: [],
  building: null,
  chat: emptyRoom(),
  chatRecent: [],
  problem: null,
  ready: false,
  loaded: new Set(),
};

/** Whether every named listener has answered once. */
export function has(...names: string[]): boolean {
  return names.every((name) => store.loaded.has(name));
}

/** What the map, the totals and the arrears all say, worked out by core. */
export function map(block?: string): UnitMap {
  const shared = {
    units: store.units,
    summaries: store.summaries,
    pending: new Set(store.waiting.map((row) => row.unitId)),
  };
  return block === undefined ? unitMap(shared) : unitMap({ ...shared, block });
}

export function behind(): Arrears {
  return arrears({
    outstanding: store.months.map((row) => ({
      unitId: row.unitId,
      period: row.summary.period,
      owed: row.summary.balance,
    })),
    blocks: new Map(store.units.map((unit) => [unit.unitId, unit.block])),
    terms: new Map(store.units.map((unit) => [unit.unitId, unit.termId])),
    currentPeriod: store.period,
  });
}

/** How far back the overview's trend reaches. Older months only count towards arrears. */
export const TREND_MONTHS = 6;

export function totals(): Overview {
  const since = addPeriods(store.period, -(TREND_MONTHS - 1));
  return overview({
    months: store.months.filter((row) => row.summary.period >= since),
    blocks: new Map(store.units.map((unit) => [unit.unitId, unit.block])),
    period: store.period,
    arrears: behind().owed,
    spent: store.expenses
      .filter((row) => !row.voided && row.period === store.period)
      .reduce((sum, row) => sum + row.amount, 0),
    held: store.held.reduce((sum, row) => sum + row.amount, 0),
  });
}

/**
 * Opens the reads this role is allowed, and calls back on every change.
 *
 * Which listeners open is decided by the role, not by hiding a screen. A
 * resident querying the verification queue is a read the rules refuse, and a
 * refusal on a screen nobody asked for would still be an error on the page.
 */
let redraw: () => void = () => undefined;
let stopUnit: data.Stop | null = null;

/**
 * Follows one unit's payments while its details are open, and stops following
 * the last one. Returns at once; the panel redraws when the rows arrive.
 */
export function followUnit(orgId: string, unitId: string | null): void {
  if (store.unitPayments?.unitId === unitId || (unitId === null && stopUnit === null)) {
    return;
  }
  stopUnit?.();
  stopUnit = null;
  store.unitPayments = null;
  if (unitId === null) {
    return;
  }
  stopUnit = data.unitPayments(
    orgId,
    unitId,
    (rows) => {
      store.unitPayments = { unitId, rows: rows.sort(byNewest) };
      redraw();
    },
    (cause) => {
      store.problem = cause;
      redraw();
    },
  );
}

/** Draws the screen again from the store, keeping what somebody was typing. */
export function refresh(): void {
  redraw();
}

let stopChat: data.Stop | null = null;

/**
 * Opens the room's listeners while the chat is on screen, and closes them when
 * it is not. `keep` holds on to what was read, for a tab that is only hidden:
 * when it comes back the listeners answer into the same screen rather than a
 * blank one.
 *
 * The four that matter to everybody, the last messages, the pinned ones, the
 * reader's membership and the room's switches, say the room is `loaded` once
 * they have all answered. The committee's list of people is a tool beside the
 * room, so a refusal there is logged rather than failing the chat.
 */
export function followChat(session: Session | null, keep = false): void {
  if (session === null) {
    stopChat?.();
    stopChat = null;
    if (!keep) {
      store.chat = emptyRoom();
      store.loaded.delete("chat");
    }
    return;
  }
  if (stopChat !== null) {
    return;
  }
  const { orgId } = session;
  const uid = session.user.uid;
  const committee = session.role === "committee";
  const waiting = new Set(["messages", "pinned", "me", "room"]);
  let joining = false;

  function answered(name: string): void {
    waiting.delete(name);
    if (waiting.size === 0) {
      store.loaded.add("chat");
    }
    redraw();
  }

  const refused = (name: string) => (cause: Error) => {
    console.error(`chat: the ${name} read was refused`, cause);
    store.chat.problem = cause;
    answered(name);
  };

  const stops = [
    data.chatMessages(
      orgId,
      (rows) => {
        store.chat.messages = rows;
        answered("messages");
      },
      refused("messages"),
    ),
    data.pinnedChat(
      orgId,
      (rows) => {
        store.chat.pinned = rows;
        answered("pinned");
      },
      refused("pinned"),
    ),
    data.chatMember(
      orgId,
      uid,
      (member) => {
        store.chat.me = member;
        // Joining is writing your own membership the first time the room
        // opens. Once: the listener answers again as soon as it lands.
        if (member === null && !joining) {
          joining = true;
          data
            .joinChat(orgId, uid, committee ? "committee" : "resident", session.unitId ?? null)
            .catch((cause: unknown) => {
              console.error("chat: could not join the room", cause);
              store.chat.problem = cause instanceof Error ? cause : new Error(String(cause));
              redraw();
            });
        }
        answered("me");
      },
      refused("me"),
    ),
    data.chatRoom(
      orgId,
      (settings) => {
        store.chat.settings = settings;
        answered("room");
      },
      refused("room"),
    ),
  ];
  if (committee) {
    stops.push(
      data.chatMembers(
        orgId,
        (rows) => {
          store.chat.members = rows;
          redraw();
        },
        (cause) => {
          console.error("chat: could not read the room's members", cause);
        },
      ),
    );
  }
  stopChat = () => stops.forEach((stop) => stop());
}

let stopOrder: data.Stop | null = null;

/** Follows the Telebirr order a resident came back from, until another replaces it. */
export function followTelebirrOrder(orderId: string): void {
  stopOrder?.();
  store.telebirrOrder = { id: orderId, order: null };
  stopOrder = data.telebirrOrder(
    orderId,
    (order) => {
      store.telebirrOrder = { id: orderId, order };
      redraw();
    },
    (cause) => {
      store.problem = cause;
      redraw();
    },
  );
}

let stopChapaOrder: data.Stop | null = null;

/** Follows the Chapa order a resident came back from, until another replaces it. */
export function followChapaOrder(orderId: string): void {
  stopChapaOrder?.();
  store.chapaOrder = { id: orderId, order: null };
  stopChapaOrder = data.chapaOrder(
    orderId,
    (order) => {
      store.chapaOrder = { id: orderId, order };
      redraw();
    },
    (cause) => {
      store.problem = cause;
      redraw();
    },
  );
}

export function watch(session: Session, changed: () => void): data.Stop {
  redraw = changed;
  const stops: data.Stop[] = [];
  store.loaded = new Set();
  const { orgId, role } = session;
  let answered = 0;
  let expected = 0;

  function settle(name: string): void {
    answered += 1;
    store.loaded.add(name);
    store.ready = answered >= expected;
    changed();
  }

  /**
   * A refusal counts as an answer.
   *
   * Not because it is one, but because a listener the rules turned down will
   * never call back, and a screen that waits for it waits forever. The
   * refusal itself is kept in `problem` and shown.
   */
  const bad = (name: string) => (cause: Error) => {
    store.problem = cause;
    settle(name);
  };

  function open(name: string, start: (done: () => void) => data.Stop): void {
    expected += 1;
    let first = true;
    stops.push(
      start(() => {
        if (first) {
          first = false;
          settle(name);
        } else {
          changed();
        }
      }),
    );
  }

  open("org", (done) =>
    data.organization(
      orgId,
      (name, entitlements, telebirr, chapa) => {
        store.orgName = name;
        store.entitlements = entitlements;
        store.telebirr = telebirr;
        store.chapa = chapa;
        done();
      },
      bad("org"),
    ),
  );
  if (session.role === "accountant" || session.role === "committee") {
    open("advertisements", (done) =>
      data.advertisements(
        orgId,
        (rows) => {
          store.advertisements = rows.sort((a, b) => a.businessName.localeCompare(b.businessName));
          done();
        },
        bad("advertisements"),
      ),
    );
  }
  open("notices", (done) =>
    data.notices(
      orgId,
      (rows) => {
        store.notices = currentNotices(rows);
        done();
      },
      bad("notices"),
    ),
  );
  /*
   * A resident's own repairs, a staff account's whole building. The rules let
   * a resident read a complaint only where the unit and the occupancy match
   * their token, and an unscoped query is refused outright rather than
   * filtered down, so the scoping has to be in the query.
   */
  const mine =
    role === "resident" && session.unitId !== undefined && session.termId !== undefined
      ? [where("unitId", "==", session.unitId), where("termId", "==", session.termId)]
      : [];
  open("complaints", (done) =>
    data.complaints(
      orgId,
      (rows) => {
        store.complaints = rows.sort(byNewest);
        done();
      },
      bad("complaints"),
      ...mine,
    ),
  );
  open("campaigns", (done) =>
    data.campaigns(
      orgId,
      (rows) => {
        store.campaigns = rows;
        done();
      },
      bad("campaigns"),
    ),
  );

  /*
   * The unread count in the band: the newest few messages, for the two roles
   * that are in the chat. Small on purpose, and outside `open` so a refusal or
   * a slow answer here never holds up a screen or blanks the console.
   */
  if (role === "resident" || role === "committee") {
    stops.push(
      data.chatMessages(
        orgId,
        (rows) => {
          store.chatRecent = rows;
          changed();
        },
        (cause) => {
          console.error("chat: could not read the newest messages for the unread count", cause);
        },
        CHAT_UNREAD_WINDOW,
      ),
    );
  }

  if (role === "resident") {
    const { unitId, termId } = session;
    open("inbox", (done) =>
      data.inbox(
        orgId,
        session.user.uid,
        (rows) => {
          store.inbox = rows;
          done();
        },
        bad("inbox"),
      ),
    );
    if (unitId !== undefined && termId !== undefined) {
      open("reminders", (done) =>
        data.reminders(
          orgId,
          unitId,
          (rows) => {
            store.reminders = rows.sort(
              (a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0),
            );
            done();
          },
          bad("reminders"),
        ),
      );
      open("payments", (done) =>
        data.paymentsFor(
          orgId,
          unitId,
          termId,
          (rows) => {
            store.mine = rows.sort(byNewest);
            done();
          },
          bad("payments"),
        ),
      );
      open("ledger", (done) =>
        data.ledgerFor(
          orgId,
          unitId,
          termId,
          (rows) => {
            store.entries = rows;
            done();
          },
          bad("ledger"),
        ),
      );
      open("extras", (done) =>
        data.extrasFor(
          orgId,
          unitId,
          termId,
          (rows) => {
            store.extras = rows;
            done();
          },
          bad("extras"),
        ),
      );
      open("unit", (done) =>
        data.monthlyDueOf(
          orgId,
          unitId,
          (monthlyDue) => {
            store.monthlyDue = monthlyDue;
            done();
          },
          bad("unit"),
        ),
      );
      open("summaries", (done) =>
        data.summariesFor(
          orgId,
          unitId,
          termId,
          (rows) => {
            store.months = rows;
            store.summaries = new Map(
              rows
                .filter((r) => r.summary.period === store.period)
                .map((r) => [r.unitId, r.summary]),
            );
            done();
          },
          bad("summaries"),
        ),
      );
    }
    return () => stops.forEach((stop) => stop());
  }

  open("units", (done) =>
    data.units(
      orgId,
      (rows) => {
        store.units = rows;
        done();
      },
      bad("units"),
    ),
  );
  open("summaries", (done) =>
    data.recentSummaries(
      orgId,
      addPeriods(store.period, -(TREND_MONTHS - 1)),
      (rows) => {
        store.months = rows;
        store.summaries = new Map(
          rows.filter((r) => r.summary.period === store.period).map((r) => [r.unitId, r.summary]),
        );
        done();
      },
      bad("summaries"),
    ),
  );
  open("waiting", (done) =>
    data.waiting(
      orgId,
      (rows) => {
        store.waiting = rows.sort(byOldest);
        done();
      },
      bad("waiting"),
    ),
  );
  open("expenses", (done) =>
    data.expenses(
      orgId,
      // A year of spending: the books, the breakdown and the statement all
      // look at recent months.
      addPeriods(store.period, -11),
      (rows) => {
        store.expenses = rows.sort(byNewest);
        done();
      },
      bad("expenses"),
    ),
  );
  open("held", (done) =>
    data.held(
      orgId,
      (rows) => {
        store.held = rows;
        done();
      },
      bad("held"),
    ),
  );

  if (role === "accountant") {
    open("accounts", (done) =>
      data.accounts(
        orgId,
        (rows) => {
          store.accounts = rows.sort((a, b) => a.name.localeCompare(b.name));
          done();
        },
        bad("accounts"),
      ),
    );
    if (session.buildingId) {
      const buildingId = session.buildingId;
      open("building", (done) =>
        data.building(
          orgId,
          buildingId,
          (row) => {
            store.building = row;
            done();
          },
          bad("building"),
        ),
      );
    }
  }

  // The journal, which the committee reads too, and who each row names.
  if (role === "accountant" || role === "committee") {
    open("chapa", (done) =>
      data.chapaReceipts(
        orgId,
        (rows) => {
          store.chapaReceipts = rows;
          done();
        },
        bad("chapa"),
      ),
    );
    open("changes", (done) =>
      data.changes(
        orgId,
        (rows) => {
          store.changes = rows;
          done();
        },
        bad("changes"),
      ),
    );
    open("people", (done) =>
      data.people(
        orgId,
        (rows, logins) => {
          store.people = rows;
          store.logins = logins;
          done();
        },
        bad("people"),
      ),
    );
  }

  return () => {
    stops.forEach((stop) => stop());
    followUnit(orgId, null);
    followChat(null);
  };
}

function byNewest(a: { at?: Date | null; submittedAt?: Date | null }, b: typeof a): number {
  return when(b) - when(a);
}

function byOldest(a: { submittedAt: Date | null }, b: typeof a): number {
  return when(a) - when(b);
}

function when(row: { at?: Date | null; submittedAt?: Date | null }): number {
  return (row.at ?? row.submittedAt)?.getTime() ?? 0;
}
